'use client';
import { useColorScheme } from '#pplx/frontend/aether/hooks/useColorScheme.js';
import { upsellDismissedEvent, upsellViewedEvent, } from '#pplx/frontend/apps/ask/events/unified-upsell/analytics.js';
import { isCtaSupported } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/handlers/executeCtaHandler.js';
import { useDismissedUpsellsStore } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useDismissedUpsellsStore.js';
import { useHomepageUpsellsQuery } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useHomepageUpsellsQuery.js';
import { COMPUTER_SETUP_CHECKLIST_UPSELL_NAME } from '#pplx/frontend/apps/ask/layout-aux/computer-setup-checklist/types.js';
import { markUpsellAsInteracted } from '#pplx/frontend/apps/ask/queries/upsell-queries/markUpsellAsInteracted.js';
import { buildUpsellSlotAnalytics, getUpsellSlotInstanceId, } from '#pplx/frontend/apps/ask/upsell-slots/UpsellSlotRenderPlan.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { EVENT_PAYLOAD, IMPRESSION_PAYLOAD, } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useEffect, useState } from 'react';
const sideUpsellDismissedEvent = EVENT_PAYLOAD;
const sideUpsellViewedEvent = IMPRESSION_PAYLOAD;
export const MOBILE_APP_DOWNLOAD_UPSELL_NAME = 'mobile_app_download';
const isSideCardUpsellRequestEnabled = (input) => input.callsite.enabled !== false &&
    input.callsite.surface?.isAllowedFloatingPath !== false &&
    input.callsite.surface?.hasPrimarySurfaceContent !== true &&
    input.callsite.surface?.hasCompetingModal !== true &&
    input.callsite.surface?.hasCookieConsentResolved !== false &&
    input.callsite.surface?.isDesktopAppBuild !== true &&
    input.callsite.surface?.isMobileLayout !== true &&
    input.callsite.surface?.isFirefoxDefaultSearch !== true;
const getComputerSetupChecklist = (upsellInformation) => upsellInformation?.name === COMPUTER_SETUP_CHECKLIST_UPSELL_NAME
    ? (upsellInformation.setup_checklist ?? null)
    : null;
const resolveSideCardUpsellSlotPlan = ({ input, data, controls, }) => {
    const enabled = input.callsite.enabled !== false && input.callsite.surface?.isAllowedFloatingPath !== false;
    if (!enabled) {
        return { status: 'empty', show: false, reason: 'disabled' };
    }
    if (input.callsite.surface?.hasPrimarySurfaceContent ||
        input.callsite.surface?.hasCompetingModal ||
        input.callsite.surface?.hasCookieConsentResolved === false) {
        return { status: 'blocked', show: false, reason: 'competing_surface' };
    }
    if (input.callsite.surface?.isMobileLayout) {
        return { status: 'blocked', show: false, reason: 'blocked_by_callsite' };
    }
    if (input.callsite.surface?.isDesktopAppBuild) {
        return { status: 'blocked', show: false, reason: 'blocked_by_callsite' };
    }
    const { upsellInformation } = data;
    if (!upsellInformation) {
        return { status: 'empty', show: false, reason: 'no_backend_upsell' };
    }
    if (!upsellInformation.app_location ||
        !input.acceptedLocations.includes(upsellInformation.app_location)) {
        return { status: 'blocked', show: false, reason: 'location_mismatch' };
    }
    if (upsellInformation.cta && !isCtaSupported(upsellInformation.cta)) {
        return { status: 'blocked', show: false, reason: 'unsupported_cta' };
    }
    if (data.hiddenUpsellInstanceId === getUpsellSlotInstanceId(upsellInformation)) {
        return { status: 'blocked', show: false, reason: 'dismissed' };
    }
    if (input.callsite.surface?.isFirefoxDefaultSearch) {
        return { status: 'blocked', show: false, reason: 'blocked_by_callsite' };
    }
    if (upsellInformation.name === MOBILE_APP_DOWNLOAD_UPSELL_NAME &&
        !input.callsite.surface?.isComputerRoute &&
        upsellInformation.title &&
        upsellInformation.description) {
        return {
            status: 'ready',
            show: true,
            slotId: input.slotId,
            upsellInformation,
            render: {
                kind: 'floating_app_download',
                componentKey: 'FloatingAppDownload',
                props: {
                    title: upsellInformation.title,
                    description: upsellInformation.description,
                    qrCodeAsset: upsellInformation.qr_code_asset,
                    onDismiss: controls.onDismiss,
                },
            },
            actions: {
                ...controls,
                primaryCta: {
                    upsellInformation,
                    textButtonIfSecondary: true,
                },
            },
            analytics: buildUpsellSlotAnalytics(input.slotId, upsellInformation),
        };
    }
    const checklist = getComputerSetupChecklist(upsellInformation);
    if (checklist?.items?.length && upsellInformation.title) {
        return {
            status: 'ready',
            show: true,
            slotId: input.slotId,
            upsellInformation,
            render: {
                kind: 'computer_setup_checklist',
                componentKey: 'ComputerSetupChecklistFloatingUpsell',
                props: {
                    checklist,
                    onDismiss: controls.onDismiss,
                    upsellInformation,
                },
            },
            actions: {
                ...controls,
                primaryCta: {
                    upsellInformation,
                    textButtonIfSecondary: true,
                },
            },
            analytics: buildUpsellSlotAnalytics(input.slotId, upsellInformation),
        };
    }
    if (input.callsite.surface?.isComputerRoute) {
        return { status: 'blocked', show: false, reason: 'blocked_by_callsite' };
    }
    if (!upsellInformation.title || !upsellInformation.description) {
        return { status: 'blocked', show: false, reason: 'missing_required_fields' };
    }
    return {
        status: 'ready',
        show: true,
        slotId: input.slotId,
        upsellInformation,
        render: {
            kind: 'floating_card',
            componentKey: 'FloatingCardUpsell',
            props: {
                shouldShow: true,
                imageSrc: data.image,
                imageAlt: 'Promotional content image',
                title: upsellInformation.title,
                description: upsellInformation.description,
                onDismiss: controls.onDismiss,
                iconXDismiss: true,
            },
        },
        actions: {
            ...controls,
            primaryCta: {
                upsellInformation,
                textButtonIfSecondary: true,
            },
        },
        analytics: buildUpsellSlotAnalytics(input.slotId, upsellInformation),
    };
};
export const useSideCardUpsellSlotPlan = (input) => {
    const enabled = isSideCardUpsellRequestEnabled(input);
    const { upsellInformation } = useHomepageUpsellsQuery({
        enabled,
        location: input.source.request.location,
    });
    const { session } = useSession();
    const { track, impression } = useInstrumentation();
    const { colorScheme } = useColorScheme();
    const markUpsellDismissed = useDismissedUpsellsStore((s) => s.markDismissed);
    const userId = session?.user?.id ?? null;
    const [hiddenUpsellInstanceId, setHiddenUpsellInstanceId] = useState(null);
    const hideUpsell = useCallback(() => {
        if (!upsellInformation)
            return;
        setHiddenUpsellInstanceId(getUpsellSlotInstanceId(upsellInformation));
        if (upsellInformation.name) {
            markUpsellDismissed(userId, upsellInformation.name);
        }
    }, [markUpsellDismissed, upsellInformation, userId]);
    const onDismiss = useCallback(() => {
        if (!enabled || !upsellInformation)
            return;
        hideUpsell();
        if (upsellInformation.upsell_type != null && upsellInformation.app_location != null) {
            track(sideUpsellDismissedEvent({
                name: 'side upsell dismissed',
                data: {
                    upsellName: upsellInformation.name ?? '',
                    upsellUuid: upsellInformation.upsell_uuid ?? '',
                    eventMetadata: upsellInformation.event_metadata,
                    upsellType: upsellInformation.upsell_type,
                },
            }));
            track(upsellDismissedEvent({
                name: 'upsell dismissed',
                data: {
                    upsellUUID: upsellInformation.upsell_uuid ?? '',
                    upsellName: upsellInformation.name ?? '',
                    eventMetadata: upsellInformation.event_metadata,
                    upsellLocation: upsellInformation.app_location,
                    upsellType: upsellInformation.upsell_type,
                },
            }));
        }
        markUpsellAsInteracted({
            upsellName: upsellInformation.name ?? '',
            upsellUUID: upsellInformation.upsell_uuid,
            interactionType: 'dismiss',
        });
    }, [enabled, hideUpsell, track, upsellInformation]);
    const image = (colorScheme === 'dark' && upsellInformation?.image_asset_dark) ||
        upsellInformation?.image_asset;
    const controls = {
        onDismiss,
        hideUpsell,
    };
    const plan = resolveSideCardUpsellSlotPlan({
        input,
        data: {
            upsellInformation,
            image,
            hiddenUpsellInstanceId,
        },
        controls,
    });
    const readyUpsellInformation = plan.show ? plan.upsellInformation : null;
    useEffect(() => {
        if (!enabled || !readyUpsellInformation || readyUpsellInformation.upsell_type == null) {
            return;
        }
        // Keep the legacy side-card event alongside the unified upsell event while analytics migrate.
        impression(sideUpsellViewedEvent({
            name: 'side upsell viewed',
            data: {
                upsellName: readyUpsellInformation.name ?? '',
                upsellUuid: readyUpsellInformation.upsell_uuid ?? '',
                eventMetadata: readyUpsellInformation.event_metadata,
                upsellType: readyUpsellInformation.upsell_type,
            },
        }));
        impression(upsellViewedEvent({
            name: 'upsell viewed',
            data: {
                upsellUUID: readyUpsellInformation.upsell_uuid ?? '',
                upsellName: readyUpsellInformation.name ?? '',
                eventMetadata: readyUpsellInformation.event_metadata,
                upsellLocation: readyUpsellInformation.app_location ?? input.slotId,
                upsellType: readyUpsellInformation.upsell_type,
            },
        }));
    }, [
        enabled,
        readyUpsellInformation,
        readyUpsellInformation?.app_location,
        readyUpsellInformation?.event_metadata,
        readyUpsellInformation?.name,
        readyUpsellInformation?.upsell_type,
        readyUpsellInformation?.upsell_uuid,
        input.slotId,
        impression,
    ]);
    return plan;
};
//# sourceMappingURL=useSideCardUpsellSlotPlan.js.map